import { useMemo } from "react";
import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import { scopeProjectRef } from "@cmmd-forge/client-runtime";
import { useStore } from "../store";
import type { TimestampFormat } from "@cmmd-forge/contracts/settings";
import {
  deriveActivePlanState,
  findSidebarProposedPlan,
  isLatestTurnSettled,
} from "../session-logic";
import {
  createProjectSelectorByRef,
  createThreadSelectorByRef,
  createThreadSelectorAcrossEnvironments,
} from "../storeSelectors";
import PlanSidebar from "./PlanSidebar";

interface PlanPanelContentProps {
  readonly threadRef: ScopedThreadRef;
  readonly onClose: () => void;
  readonly timestampFormat: TimestampFormat;
}

export function PlanPanelContent({ threadRef, onClose, timestampFormat }: PlanPanelContentProps) {
  const thread = useStore(useMemo(() => createThreadSelectorByRef(threadRef), [threadRef]));

  const activeLatestTurn = thread?.latestTurn ?? null;
  const threadActivities = useMemo(() => thread?.activities ?? [], [thread?.activities]);

  const sourceThreadId = activeLatestTurn?.sourceProposedPlan?.threadId ?? null;
  const sourceThreadSelector = useMemo(
    () => createThreadSelectorAcrossEnvironments(sourceThreadId ?? undefined),
    [sourceThreadId],
  );
  const sourceThread = useStore(sourceThreadSelector);

  const threadPlanCatalog = useMemo(() => {
    const entries = [];
    if (thread) entries.push(thread);
    if (sourceThread && sourceThread.id !== thread?.id) entries.push(sourceThread);
    return entries;
  }, [thread, sourceThread]);

  const latestTurnSettled = isLatestTurnSettled(activeLatestTurn, thread?.session ?? null);

  const activePlan = useMemo(
    () => deriveActivePlanState(threadActivities, activeLatestTurn?.turnId ?? undefined),
    [activeLatestTurn?.turnId, threadActivities],
  );

  const sidebarProposedPlan = useMemo(
    () =>
      findSidebarProposedPlan({
        threads: threadPlanCatalog,
        latestTurn: activeLatestTurn,
        latestTurnSettled,
        threadId: thread?.id ?? null,
      }),
    [activeLatestTurn, latestTurnSettled, thread?.id, threadPlanCatalog],
  );

  const projectRef = thread ? scopeProjectRef(thread.environmentId, thread.projectId) : null;
  const project = useStore(useMemo(() => createProjectSelectorByRef(projectRef), [projectRef]));

  const gitCwd = project?.cwd ?? undefined;
  const workspaceRoot = thread?.worktreePath ?? project?.cwd ?? undefined;

  return (
    <PlanSidebar
      activePlan={activePlan}
      activeProposedPlan={sidebarProposedPlan}
      environmentId={threadRef.environmentId}
      markdownCwd={gitCwd}
      workspaceRoot={workspaceRoot}
      timestampFormat={timestampFormat}
      onClose={onClose}
      embedded
    />
  );
}
